import { api } from '../api/client';

import { API_DRAFTS_URL, getUserId, isTokenValid } from '../utils';
import { getSessionToken } from '../utils/session';
import { normalizeId } from '../utils/ids';

/**
 * Item drafts — a person's unsaved new-item forms, kept on the server and
 * private to their author (keepr-api docs/SCHEMA.md § Item drafts). They
 * used to live in localStorage under `keepr.draft.<collection>.<card>`,
 * where they survived sign-out on a shared device; the functions at the
 * bottom move what is left there to the server once, and clear it on
 * sign-out.
 *
 * Nothing here caches: every list is fetched for the account signed in at
 * the moment of asking, so a second account in the same tab can never be
 * shown the first one's drafts.
 */

const LOCAL_PREFIX = 'keepr.draft.';
const LOCAL_KEY = /^keepr\.draft\.([0-9a-f]{24})\.([0-9a-f]{24})$/i;

// Anything that shows drafts subscribes here, so deleting one on the Drafts
// page (or saving the item it became) refreshes a count elsewhere without a
// context. A nudge to refetch — never the data itself.
const listeners = new Set();

export function subscribeToDraftChanges(listener) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

function notifyDraftChange() {
	listeners.forEach(listener => {
		try { listener(); } catch { /* one bad listener must not stop the rest */ }
	});
}

/**
 * The caller's drafts, newest first, without their values. `{ drafts, total }`.
 * Each row: `{ _id, collection_id, card_id, title, filled, createdAt,
 * updatedAt, expiresAt, available, collection?, card? }`.
 */
export async function listDrafts({ collectionId, cardId, limit } = {}) {
	const params = {};
	if (collectionId) params.collection_id = collectionId;
	if (cardId) params.card_id = cardId;
	if (limit) params.limit = limit;
	const { data } = await api.get(API_DRAFTS_URL, { params });
	const drafts = Array.isArray(data?.drafts) ? data.drafts : [];
	return { drafts, total: Number.isFinite(data?.total) ? data.total : drafts.length };
}

/** One draft with its `elements` and `notes`. */
export async function getDraft(id) {
	const { data } = await api.get(`${API_DRAFTS_URL}/${encodeURIComponent(id)}`);
	return data;
}

/** `{ collection_id, card_id, elements, notes?, savedAt? }` → the new draft (no values). Never `tags`: retired (docs/plans/tags.md §4.1). */
export async function createDraft(payload) {
	const { data } = await api.post(API_DRAFTS_URL, payload);
	notifyDraftChange();
	return data;
}

/** Autosave: `{ elements?, notes?, tagIds?, privateTagIds? }`, each replacing the stored one (the tag lists since tags T9). */
export async function updateDraft(id, payload) {
	const { data } = await api.patch(`${API_DRAFTS_URL}/${encodeURIComponent(id)}`, payload);
	return data;
}

/** Deletes one of the caller's drafts. A 404 (already gone) counts as done. */
export async function deleteDraft(id) {
	try {
		await api.delete(`${API_DRAFTS_URL}/${encodeURIComponent(id)}`);
	} catch (error) {
		if (error?.response?.status !== 404) throw error;
	}
	notifyDraftChange();
}

// ---------------------------------------------------------------- the move out of localStorage

function localDraftKeys() {
	const keys = [];
	try {
		for (let i = 0; i < window.localStorage.length; i += 1) {
			const key = window.localStorage.key(i);
			if (key && key.startsWith(LOCAL_PREFIX)) keys.push(key);
		}
	} catch { /* storage blocked: nothing to move */ }
	return keys;
}

function removeLocal(key) {
	try { window.localStorage.removeItem(key); } catch { /* storage blocked */ }
}

/**
 * Sign-out: the drafts this browser still holds from before the move go
 * with the session. They are the reason drafts moved (a shared device kept
 * the last person's half-typed record for whoever came next).
 */
export function clearLocalDrafts() {
	localDraftKeys().forEach(removeLocal);
}

let migratedToken = null;

/**
 * The one-time move: a `keepr.draft.*` entry becomes a server draft of the
 * signed-in account ONLY when it names that account — `userId` in the entry
 * equal to the token's. Anything else is DELETED, not moved: every entry an
 * older build wrote lacks a `userId` (it never recorded whose it was), and a
 * draft that might be somebody else's must not be handed to whoever signs in
 * next on a shared device for thirty days.
 *
 * Not a device-clock rule. The last one moved entries saved after the
 * token's `iat`, which a wrong clock, a token refreshed by a password change
 * (every older draft suddenly "before this sign-in") or a second account in
 * another tab all broke. Ownership is a fact the entry carries or does not.
 * Nothing in this build writes a local draft; anything that ever does again
 * must stamp `userId` on every entry, or it is thrown away here.
 *
 * Per entry: moved → removed; refused (400/403/404 — the card is gone,
 * access lost, too large) → removed, since retrying cannot help; failed
 * (the network, a 5xx, a 401 or 429) → kept, and tried again on the next
 * load. Runs once per token per page load; a re-run after a token refresh
 * finds only entries it could not send, since the userId does not change.
 * Resolves to `{ moved, dropped, kept }`.
 */
export async function migrateLocalDrafts() {
	const result = { moved: 0, dropped: 0, kept: 0 };
	if (!isTokenValid()) return result;
	const token = getSessionToken();
	if (!token || migratedToken === token) return result;
	const userId = normalizeId(getUserId());
	if (!userId || userId === 'undefined') return result;
	migratedToken = token;

	for (const key of localDraftKeys()) {
		const match = LOCAL_KEY.exec(key);
		let stored = null;
		try { stored = JSON.parse(window.localStorage.getItem(key) || 'null'); } catch { stored = null; }
		const elements = stored?.elements && typeof stored.elements === 'object' && !Array.isArray(stored.elements)
			? stored.elements
			: null;
		const theirs = Boolean(stored?.userId) && normalizeId(stored.userId) === userId;
		if (!match || !elements || !Object.keys(elements).length || !theirs) {
			removeLocal(key);
			result.dropped += 1;
			continue;
		}
		const savedAt = Number(stored?.savedAt);
		try {
			// eslint-disable-next-line no-await-in-loop
			await api.post(API_DRAFTS_URL, {
				collection_id: match[1],
				card_id: match[2],
				elements,
				...(Number.isFinite(savedAt) ? { savedAt: new Date(savedAt).toISOString() } : {}),
			});
			removeLocal(key);
			result.moved += 1;
		} catch (error) {
			const status = error?.response?.status;
			// A refusal about THIS draft: retrying cannot help. A 401 or a
			// 429 is about the moment, and is tried again next load.
			if ([400, 403, 404, 409, 413].includes(status)) {
				removeLocal(key);
				result.dropped += 1;
			} else {
				result.kept += 1;
			}
		}
	}
	if (result.moved) notifyDraftChange();
	return result;
}

/** Test hook: let the move run again. */
export function _resetDraftMigration() {
	migratedToken = null;
}
